import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { DATE_FORMAT } from "utils/constants";
import {
  isDateDisabledByPickerRule,
  isStrictDisplayDateString,
  parseStrictDisplayDate,
} from "utils/helpers/date";

const useStrictDatePickerInput = ({
  format = DATE_FORMAT,
  invalidDateMessage,
  disallowedDateMessage,
  disabledDate,
  onChange,
  onBlur,
  value,
}) => {
  const [inputError, setInputError] = useState(null);
  const rawInputRef = useRef("");
  const inputModeRef = useRef("picker");

  useEffect(() => {
    if (value && dayjs(value).isValid()) {
      rawInputRef.current = dayjs(value).format(format);
      setInputError(null);
      return;
    }

    if (!value && !inputError) {
      rawInputRef.current = "";
    }
  }, [format, inputError, value]);

  const validateRawInput = useCallback(
    (raw) => {
      if (!raw) {
        return { valid: true };
      }

      if (!isStrictDisplayDateString(raw, format)) {
        return { valid: false, message: invalidDateMessage };
      }

      const parsed = parseStrictDisplayDate(raw, format);
      if (isDateDisabledByPickerRule(parsed, disabledDate)) {
        return { valid: false, message: disallowedDateMessage };
      }

      return { valid: true, parsed, raw };
    },
    [disabledDate, disallowedDateMessage, format, invalidDateMessage],
  );

  const applyMask = useCallback((event) => {
    inputModeRef.current = "typing";
    const input = event.target;
    const digits = input.value.replace(/\D/g, "").slice(0, 8);
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    let formattedDate = day;
    if (digits.length > 2) formattedDate = `${day}/${month}`;
    if (digits.length > 4) formattedDate = `${day}/${month}/${year}`;
    input.value = formattedDate;
    rawInputRef.current = formattedDate;
  }, []);

  const handleCalendarChange = useCallback(() => {
    inputModeRef.current = "picker";
  }, []);

  const rejectInput = useCallback(
    (raw, message) => {
      setInputError(message);
      rawInputRef.current = raw;
      onChange?.(null, "");
    },
    [onChange],
  );

  const commitRawInput = useCallback(
    (raw) => {
      rawInputRef.current = raw;

      if (!raw) {
        setInputError(null);
        return { valid: true };
      }

      const validation = validateRawInput(raw);
      if (!validation.valid) {
        rejectInput(raw, validation.message);
        return validation;
      }

      setInputError(null);
      onChange?.(validation.parsed, validation.raw);
      return validation;
    },
    [onChange, rejectInput, validateRawInput],
  );

  const handleDateChange = useCallback(
    (nextValue, dateString) => {
      if (!nextValue) {
        if (rawInputRef.current.trim()) {
          return;
        }

        setInputError(null);
        rawInputRef.current = "";
        onChange?.(nextValue, dateString);
        return;
      }

      if (inputModeRef.current === "picker") {
        if (isDateDisabledByPickerRule(nextValue, disabledDate)) {
          rejectInput(
            dateString || dayjs(nextValue).format(format),
            disallowedDateMessage,
          );
          return;
        }

        setInputError(null);
        rawInputRef.current = dateString || dayjs(nextValue).format(format);
        onChange?.(nextValue, dateString);
        return;
      }

      const raw = rawInputRef.current.trim();
      const validation = validateRawInput(raw);
      if (!validation.valid) {
        rejectInput(raw, validation.message);
        return;
      }

      setInputError(null);
      onChange?.(validation.parsed, validation.raw);
    },
    [
      disabledDate,
      disallowedDateMessage,
      format,
      onChange,
      rejectInput,
      validateRawInput,
    ],
  );

  const handleBlur = useCallback(
    (event) => {
      const raw = event?.target?.value?.trim() || "";
      commitRawInput(raw);
      onBlur?.(event);
    },
    [commitRawInput, onBlur],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      inputModeRef.current = "typing";
      const raw = event?.target?.value?.trim() || "";
      commitRawInput(raw);
      onBlur?.(event);
    },
    [commitRawInput, onBlur],
  );

  return {
    inputError,
    applyMask,
    handleCalendarChange,
    handleDateChange,
    handleBlur,
    handleKeyDown,
  };
};

export default useStrictDatePickerInput;
